import { createEffect, createSignal, onCleanup } from 'solid-js';
import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { type Web3DEventName, type Web3DViewer } from '@technology/web3d';
import {
  ICADObject,
  LineEditingModel,
} from '@technology/web3d-plugin-line-editing';
import {
  CAD_FEATURE_ID_KEY,
  CAD_MIRROR_CULL_DISTANCE_METERS,
  CAD_SOURCE_FEATURE_KEY,
  FeatureCadTransformer,
  GeospatialFeature,
  selectCadMirroredFeatureIds,
  type ScenePoint,
} from '@trimble/geospatial';
import { CadSceneProjection } from './cad-scene-projection';

const NAVIGATION_END_EVENT = 'navigationend' as Web3DEventName.NavigationEnd;

export interface CreateFeatureCadMirrorParams {
  readonly viewer: Web3DViewer;
  readonly model: LineEditingModel;
  readonly context: FeatureExtractionContextType;
  readonly transformer: FeatureCadTransformer;
  readonly projection: Pick<
    CadSceneProjection,
    'converterSettled' | 'converter'
  >;
}

export interface FeatureCadMirror {
  dispose(): void;
}

export function createFeatureCadMirror(
  params: CreateFeatureCadMirrorParams
): FeatureCadMirror {
  const { viewer, model, context, transformer, projection } = params;
  const mirroredById = new Map<string, { guid: string; signature: string }>();

  let disposed = false;

  const dispose = (): void => {
    if (disposed) {
      return;
    }
    disposed = true;
    viewer.removeEventListener(NAVIGATION_END_EVENT, onNavigationEnd);
    // Drop the claim first so the features only this camera held are released
    // now, rather than lingering until some other pane happens to re-evaluate.
    dropRetentionRegion();
    context.evictDistantFeatures();
    mirroredById.clear();
  };

  // performAction throws on undefined — drop guids already gone from the model.
  const resolveRemovals = (...guids: string[]): ICADObject[] =>
    (model.getEntitiesByGuids(...guids) as (ICADObject | undefined)[]).filter(
      (entity): entity is ICADObject => entity !== undefined
    );

  const indexStampedEntities = (): Map<string, ICADObject> => {
    const byFeatureId = new Map<string, ICADObject>();
    for (const entity of model.allEntities()) {
      const id = entity.metaData[CAD_FEATURE_ID_KEY];
      if (typeof id === 'string' && id.length > 0) {
        byFeatureId.set(id, entity);
      }
    }
    return byFeatureId;
  };

  const removeMirroredFeature = (id: string): void => {
    const record = mirroredById.get(id);
    mirroredById.delete(id);
    if (!record) {
      return;
    }
    const removals = resolveRemovals(record.guid);
    if (removals.length > 0) {
      model.performAction([], removals, false);
    }
  };

  const syncMirroredFeature = (
    id: string,
    stampedEntities: Map<string, ICADObject>
  ): void => {
    const feature = context.getFeature(id);
    if (!feature) {
      removeMirroredFeature(id);
      return;
    }
    const signature = geometrySignature(feature);
    const existing = mirroredById.get(id);
    if (existing && existing.signature === signature) {
      return;
    }

    const stamped = stampedEntities.get(id);
    if (stamped && cadSourceSignature(stamped) === signature) {
      mirroredById.set(id, { guid: stamped.guid, signature });
      return;
    }
    const entity = transformer.toCadObjectFromFeature(feature);
    if (!entity) {
      return;
    }
    const removals = existing ? resolveRemovals(existing.guid) : [];
    model.performAction([entity], removals, false);
    mirroredById.set(id, { guid: entity.guid ?? id, signature });
  };

  const readCameraReference = (): ScenePoint => ({
    x: viewer.camera.position.x,
    y: viewer.camera.position.y,
    z: viewer.camera.position.z,
  });

  const [cameraEpoch, setCameraEpoch] = createSignal(0);
  const onNavigationEnd = (): void => {
    setCameraEpoch((epoch) => epoch + 1);
  };
  viewer.addEventListener(NAVIGATION_END_EVENT, onNavigationEnd);
  onCleanup(() => {
    viewer.removeEventListener(NAVIGATION_END_EVENT, onNavigationEnd);
  });

  // Only the feature being edited is pinned; pinning what is already mirrored
  // would make it permanently immune to culling and eviction.
  const readPinnedFeatureIds = (): ReadonlySet<string> => {
    const selectedId = context.editingState.selectedFeature?.id;
    return new Set(selectedId ? [selectedId] : []);
  };

  const dropRetentionRegion = context.registerFeatureRetention(() => ({
    reference: readCameraReference(),
    pinnedFeatureIds: readPinnedFeatureIds(),
  }));
  onCleanup(dropRetentionRegion);

  const readVisibleLayerIds = (): Set<string> =>
    new Set(
      context.editingState.layers
        .filter((layer) => context.isLayerVisible(layer.id))
        .map((layer) => layer.id)
    );

  // Wait for a real converter — identity projection places features at raw lon/lat.
  createEffect(() => {
    const ids = context.featureIds();
    const settled = projection.converterSettled();
    cameraEpoch();
    if (disposed) {
      return;
    }
    if (!settled || !projection.converter()) {
      return;
    }
    const reference = readCameraReference();
    const pinnedFeatureIds = readPinnedFeatureIds();
    // Every live pane votes, so this never drops features another camera needs.
    context.evictDistantFeatures();
    const mirroredIds = selectCadMirroredFeatureIds({
      features: ids.map((id) => {
        const feature = context.getFeature(id);
        return { id, layerId: feature?.layerId ?? '' };
      }),
      sceneAnchors: context.getSceneAnchors(),
      reference,
      cullDistanceMeters: CAD_MIRROR_CULL_DISTANCE_METERS,
      visibleLayerIds: readVisibleLayerIds(),
      pinnedFeatureIds,
    });
    const stampedEntities = indexStampedEntities();
    const target = new Set(mirroredIds);
    for (const id of Array.from(mirroredById.keys())) {
      if (!target.has(id)) {
        removeMirroredFeature(id);
      }
    }
    for (const id of target) {
      syncMirroredFeature(id, stampedEntities);
    }
  });

  return { dispose };
}

function geometrySignature(feature: GeospatialFeature): string {
  return JSON.stringify(feature.geometry);
}

function cadSourceSignature(entity: ICADObject): string | undefined {
  const source = entity.metaData[CAD_SOURCE_FEATURE_KEY];
  if (typeof source === 'object' && source !== null && 'geometry' in source) {
    return JSON.stringify(source.geometry);
  }
  return undefined;
}
